"use client";
import React, { useEffect, useRef, useContext, useState } from "react";
import { ThemeContext } from "@/context/ThemeContext";
import { useSession, signOut } from "next-auth/react";
import { useNavigation } from "@/context/NavigationContext";
import FontSizeController from "./FontSizeController";

export default function Navbar() {
  const { data, status } = useSession();
  const { theme, changeTheme } = useContext(ThemeContext);
  const [isDarkMode, setIsDarkMode] = useState<boolean>(theme === "dark");
  const personasRef = useRef<HTMLDivElement | null>(null);
  const { handleNavigation } = useNavigation();

  const handleThemeToggle = (): void => {
    const newTheme = isDarkMode ? "light" : "dark";
    setIsDarkMode(!isDarkMode);
    changeTheme(newTheme);
  };

  const getInitials = (name: string | null | undefined): string => {
    if (!name) return "U";
    const words = name.trim().split(" ");
    return words.slice(0, 2).map(word => word[0].toUpperCase()).join("");
  };

  useEffect(() => {
    const handleDocumentClick = (e: MouseEvent): void => {
      // If the click is outside the details element, close it
      if (personasRef.current && !personasRef.current.contains(e.target as Node)) {
        personasRef.current.removeAttribute("open");
      }
    };

    document.addEventListener("click", handleDocumentClick);
    return () => document.removeEventListener("click", handleDocumentClick);
  }, []);

  return (
    <div className="navbar bg-base-100 shadow-md">
      <div className="navbar-start flex items-center gap-1">
        {status === "authenticated" && (
          <div className="dropdown">
            <button
              type="button"
              tabIndex={0}
              className="btn btn-ghost px-1 lg:hidden"
              aria-label="Open main menu"
              aria-haspopup="true"
              aria-controls="main-menu-dropdown"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16m-7 6h7"
                />
              </svg>
            </button>
            <ul
              id="main-menu-dropdown"
              tabIndex={0}
              className="menu menu-sm dropdown-content mt-3 z-[1] p-2 shadow bg-base-100 rounded-box w-52"
            >
              <li>
                <button onClick={() => handleNavigation("/scenarios")}>Scenarios</button>
              </li>
              <li>
                <button onClick={() => handleNavigation("/consultations")}>Consultations</button>
              </li>
            </ul>
          </div>
        )}
        <button onClick={() => handleNavigation("/")} className="btn btn-ghost text-xl sm:px-2">
          CaseConnect AI
        </button>
      </div>

      {status === "authenticated" && (
        <>
          <div className="navbar-center hidden lg:flex">
            <ul className="menu menu-horizontal px-1 z-10">
              <li>
                <button className="btn btn-ghost"
                  onClick={() => handleNavigation("/scenarios")}>Scenarios</button>
              </li>
              <li className="ml-2">
                <button className="btn btn-ghost"
                  onClick={() => handleNavigation("/consultations")}>Consultations</button>
              </li>
            </ul>
          </div>

          <div className="navbar-end flex items-center">
            <FontSizeController />

            {/* Themes toggle */}
            <div
              className="lg:tooltip lg:tooltip-bottom lg:tooltip-primary font-normal lg:mr-2"
              data-tip={isDarkMode ? "Change to light theme" : "Change to dark theme"}
            >
              <label
                tabIndex={0}
                role="button"
                className="btn btn-ghost btn-circle swap swap-rotate items-center cursor-pointer mr-2"
                aria-label="Toggle theme"
              >
                <input
                  type="checkbox"
                  checked={isDarkMode}
                  onChange={handleThemeToggle}
                  className="hidden"
                />
                {/* Sun Icon – Light Mode */}
                <svg
                  className="swap-off fill-current w-6 h-6"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  aria-label="Sun Icon"
                >
                  <path d="M5.64,17l-.71.71a1,1,0,0,0,0,1.41,1,1,0,0,0,1.41,0l.71-.71A1,1,0,0,0,5.64,17ZM5,12a1,1,0,0,0-1-1H3a1,1,0,0,0,0,2H4A1,1,0,0,0,5,12Zm7-7a1,1,0,0,0,1-1V3a1,1,0,0,0-2,0V4A1,1,0,0,0,12,5ZM5.64,7.05a1,1,0,0,0,.7.29,1,1,0,0,0,.71-.29,1,1,0,0,0,0-1.41l-.71-.71A1,1,0,0,0,4.93,6.34Zm12,.29a1,1,0,0,0,.7-.29l.71-.71a1,1,0,1,0-1.41-1.41L17,5.64a1,1,0,0,0,0,1.41A1,1,0,0,0,17.66,7.34ZM21,11H20a1,1,0,0,0,0,2h1a1,1,0,0,0,0-2Zm-9,8a1,1,0,0,0-1,1v1a1,1,0,0,0,2,0V20A1,1,0,0,0,12,19ZM18.36,17A1,1,0,0,0,17,18.36l.71.71a1,1,0,0,0,1.41,0,1,1,0,0,0,0-1.41ZM12,6.5A5.5,5.5,0,1,0,17.5,12,5.51,5.51,0,0,0,12,6.5Zm0,9A3.5,3.5,0,1,1,15.5,12,3.5,3.5,0,0,1,12,15.5Z" />
                </svg>

                {/* Moon Icon – Dark Mode */}
                <svg
                  className="swap-on fill-current w-6 h-6"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  aria-label="Moon Icon"
                >
                  <path d="M21.64,13a1,1,0,0,0-1.05-.14,8.05,8.05,0,0,1-3.37.73A8.15,8.15,0,0,1,9.08,5.49a8.59,8.59,0,0,1,.25-2A1,1,0,0,0,8,2.36,10.14,10.14,0,1,0,22,14.05A1,1,0,0,0,21.64,13Zm-9.5,6.69A8.14,8.14,0,0,1,7.08,5.22v.27A10.15,10.15,0,0,0,17.22,15.63a9.79,9.79,0,0,0,2.1-.22A8.11,8.11,0,0,1,12.14,19.73Z" />
                </svg>
              </label>
            </div>

            <div className="dropdown dropdown-end">
              <div
                tabIndex={0}
                role="button"
                className="btn btn-ghost btn-circle avatar"
              >
                <div className="w-10 rounded-full flex items-center justify-center">
                  {data.user.image ? (
                    <img alt="Your profile image" src={data.user.image} />
                  ) : (
                    <div className="ring-primary ring-offset-base-100 w-10 h-16 bg-base-200 text-blue-700 rounded-full ring ring-offset-2">
                      <span className="text-3xl">
                        {getInitials(data.user.name)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <ul
                tabIndex={0}
                className="menu menu-sm dropdown-content bg-base-100 rounded-box z-[1] mt-3 w-52 p-2 shadow"
              >
                <li>
                  <a onClick={() => signOut({ callbackUrl: "/" })}>Logout</a>
                </li>
              </ul>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
